import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  IconButton,
  useTheme,
  Skeleton
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Description as DescriptionIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { proposalsAPI, customersAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalProposals: 0,
    totalCustomers: 0,
    totalRevenue: 0,
    pendingProposals: 0,
    acceptedProposals: 0,
    rejectedProposals: 0,
    statusOverview: {
      draft: 0,
      sent: 0,
      pending: 0,
      accepted: 0,
      declined: 0,
      expired: 0
    },
    recentProposals: [],
    recentCustomers: []
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch proposal stats
      const statsResponse = await proposalsAPI.getStats();
      
      // Fetch recent proposals
      const proposalsResponse = await proposalsAPI.getAll({ 
        limit: 5, 
        sortBy: 'createdAt', 
        sortOrder: 'DESC' 
      });
      
      // Fetch recent customers
      const customersResponse = await customersAPI.getAll({ 
        limit: 5, 
        sortBy: 'createdAt', 
        sortOrder: 'DESC' 
      });
      
      // Update stats with all data
      setStats({
        ...statsResponse.data,
        statusOverview: statsResponse.data.statusOverview || {
          draft: 0,
          sent: 0,
          pending: 0,
          accepted: 0,
          declined: 0,
          expired: 0
        },
        recentProposals: proposalsResponse.data.proposals || [],
        recentCustomers: customersResponse.data.customers || []
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    
    // Set up interval to refresh data every 1 minute
    const refreshInterval = setInterval(() => {
      fetchDashboardData();
    }, 60000); // Changed from 15000 to 60000 (1 minute)
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [fetchDashboardData]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'sent': return 'info';
      case 'opened': return 'primary';
      case 'viewed': return 'warning';
      case 'accepted': return 'success';
      case 'declined': return 'error';
      case 'rejected': return 'error';
      case 'expired': return 'secondary';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const StatCard = ({ title, value, icon, color, trend, trendValue, loading: cardLoading }) => (
    <Card
      elevation={2}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            {cardLoading ? (
              <Skeleton variant="text" width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={700} color={color}>
                {value}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend === 'up' ? (
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 16, mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 16, mr: 0.5 }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600
                  }}
                >
                  {trendValue}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              backgroundColor: color,
              width: 56,
              height: 56
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {getGreeting()}, {user?.firstName}!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your proposals today.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/proposals/new')}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            New Proposal
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Proposals"
            value={stats.totalProposals}
            icon={<DescriptionIcon />}
            color={theme.palette.primary.main}
            loading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Customers"
            value={stats.totalCustomers}
            icon={<PeopleIcon />}
            color={theme.palette.secondary.light}
            loading={refreshing}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pending Proposals"
            value={stats.pendingProposals}
            icon={<EmailIcon />}
            color={theme.palette.warning.main}
            loading={refreshing}
          />
        </Grid>
      </Grid>

      {/* Status Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Proposal Status Overview
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Draft</Typography>
                  <Chip
                    label={stats.statusOverview.draft}
                    color="default"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Sent</Typography>
                  <Chip
                    label={stats.statusOverview.sent}
                    color="info"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Pending</Typography>
                  <Chip
                    label={stats.pendingProposals}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2">Accepted</Typography>
                  <Chip
                    label={stats.acceptedProposals}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Quick Actions
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/proposals/new')}
                    sx={{ py: 1.5, textTransform: 'none' }}
                  >
                    New Proposal
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/customers/new')}
                    sx={{ py: 1.5, textTransform: 'none' }}
                  >
                    Add Customer
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DescriptionIcon />}
                    onClick={() => navigate('/templates')}
                    sx={{ py: 1.5, textTransform: 'none' }}
                  >
                    Templates
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => navigate('/proposals')}
                    sx={{ py: 1.5, textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        {/* Recent Proposals */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Proposals
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/proposals')}
                  sx={{ textTransform: 'none' }}
                >
                  View All
                </Button>
              </Box>
              {stats.recentProposals.length > 0 ? (
                <List sx={{ py: 0 }}>
                  {stats.recentProposals.map((proposal, index) => (
                    <ListItem
                      key={proposal.id}
                      sx={{
                        px: 0,
                        borderBottom: index < stats.recentProposals.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                          <DescriptionIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {proposal.customer?.companyName || proposal.customer?.contactPerson || 'Unknown Customer'} - {proposal.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                              color={getStatusColor(proposal.status)}
                              size="small"
                              sx={{ 
                                fontSize: '0.75rem', 
                                fontWeight: 600, 
                                height: 20,
                                minWidth: '80px',
                                textAlign: 'center',
                                '& .MuiChip-label': {
                                  textAlign: 'center',
                                  width: '100%'
                                }
                              }}
                            />

                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  No recent proposals
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Customers */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Customers
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/customers')}
                  sx={{ textTransform: 'none' }}
                >
                  View All
                </Button>
              </Box>
              {stats.recentCustomers.length > 0 ? (
                <List sx={{ py: 0 }}>
                  {stats.recentCustomers.map((customer, index) => (
                    <ListItem
                      key={customer.id}
                      sx={{
                        px: 0,
                        borderBottom: index < stats.recentCustomers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: theme.palette.info.main }}>
                          {(() => {
                            const name = customer.contactPerson || customer.companyName || '';
                            const nameParts = name.trim().split(' ');
                            if (nameParts.length >= 2) {
                              return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
                            } else if (nameParts.length === 1 && nameParts[0]) {
                              return nameParts[0].charAt(0).toUpperCase();
                            }
                            return '?';
                          })()} 
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {customer.contactPerson || customer.companyName || 'Unknown Customer'}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {customer.email || 'No email provided'}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                  No recent customers
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CSS for refresh animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default Dashboard;